import type { NodeEntry } from '@udecode/plate';
import { useEditorPlugin, useEditorRef, usePluginOption } from '@udecode/plate/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TFootnoteElement } from '../../lib';
import { FootnotesPlugin } from '../FootnotesPlugin';

export function useFootnoteOpenStateForElement(element: TFootnoteElement) {
    const editor = useEditorPlugin(FootnotesPlugin);
    const openedId = usePluginOption(FootnotesPlugin, '_openedFootnoteId');

    const isOpened = openedId === element.id;

    const open = useCallback(() => {
        editor.api.footnotes.open(element.id ?? '__none__');
    }, [ element, editor ]);

    const close = useCallback(() => {
        editor.api.footnotes.close();
    }, [ editor ]);

    return {
        isOpened,
        open,
        close
    };
}

export function useFootnoteOpenState() {
    const editor = useEditorRef();
    const plugin = useEditorPlugin(FootnotesPlugin);
    const openedId = usePluginOption(FootnotesPlugin, '_openedFootnoteId');

    const [ prevId, setPrevId ] = useState<string|null>(null);
    const [ nextId, setNextId ] = useState<string|null>(null);

    const isOpened = openedId !== null;

    const open = useCallback((id: string) => {
        plugin.api.footnotes.open(id ?? '__none__');
    }, [ plugin ]);

    const close = useCallback(() => {
        plugin.api.footnotes.close();
    }, [ plugin ]);

    const nodeWithElement = useMemo(() => {
        let nodeEntry: NodeEntry<TFootnoteElement>|null = null;
        let domEl: HTMLElement|null = null;

        if (openedId) {
            const all = plugin.api.footnotes.getAll();
            nodeEntry = all.find(([n]) => n.id === openedId) ?? null;
        }

        if (nodeEntry) {
            domEl = editor.api.toDOMNode(nodeEntry[0]) ?? null;
        }

        return { nodeEntry, domEl };
    }, [ plugin, openedId ]);

    useEffect(() => {
        if (openedId) {
            const allFootnotes = plugin.api.footnotes.getAll();
            const currentIndex = allFootnotes.findIndex((footnote) => footnote[0].id === openedId);

            setPrevId(currentIndex > 0 ? allFootnotes[currentIndex - 1][0].id ?? null : null);
            setNextId(currentIndex < allFootnotes.length - 1 ? allFootnotes[currentIndex + 1][0].id ?? null : null);
        } else {
            setPrevId(null);
            setNextId(null);
        }
    }, [ openedId ]);

    return {
        ...nodeWithElement,
        isOpened,
        prevId,
        nextId,
        open,
        close
    };
}
